import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { AlertTriangle, X } from 'lucide-react'
import {
  calculateWorkingDays,
  listHolidaysInRange,
  VACATION_PAYMENT_DISCLAIMER,
} from '@czas/shared'

import Button from '@/components/ui/Button'
import Label from '@/components/ui/Label'
import { apiErrorMessage } from '@/lib/api'
import { useSession } from '@/lib/auth'
import { cn } from '@/lib/cn'

import PaymentBadge from '@/features/vacation/payment-badge'
import {
  useCreateMyVacationRequest,
  useMyVacationBalance,
  type VacationBalanceResponse,
} from '@/features/vacation/use-vacation'

const TYPE_LABELS: Record<
  'WYPOCZYNKOWY' | 'NA_ZADANIE' | 'BEZPLATNY' | 'OPIEKA_NAD_DZIECKIEM',
  string
> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  BEZPLATNY: 'Bezpłatny',
  OPIEKA_NAD_DZIECKIEM: 'Opieka nad dzieckiem',
}

const schema = z
  .object({
    type: z.enum([
      'WYPOCZYNKOWY',
      'NA_ZADANIE',
      'BEZPLATNY',
      'OPIEKA_NAD_DZIECKIEM',
    ]),
    startDate: z.string().min(1, 'Wybierz datę rozpoczęcia'),
    endDate: z.string().min(1, 'Wybierz datę zakończenia'),
    comment: z.string().max(500, 'Maksymalnie 500 znaków').optional(),
  })
  .refine((v) => v.endDate >= v.startDate, {
    path: ['endDate'],
    message: 'Data zakończenia musi być nie wcześniejsza niż rozpoczęcia',
  })

type FormValues = z.infer<typeof schema>

function parseIsoDate(s: string): Date {
  const [y, m, d] = s.split('-').map(Number)
  return new Date(Date.UTC(y!, m! - 1, d!))
}

function formatHumanDate(iso: string): string {
  return format(parseIsoDate(iso), 'd MMMM', { locale: pl })
}

function todayIso(): string {
  const now = new Date()
  const tzOffset = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - tzOffset).toISOString().slice(0, 10)
}

interface QuotaInfo {
  ok: boolean
  message: string | null
  remaining: number | null
}

function computeQuota(
  type: FormValues['type'],
  workingDays: number,
  balance: VacationBalanceResponse | undefined,
): QuotaInfo {
  if (!balance) return { ok: true, message: null, remaining: null }
  if (workingDays === 0) return { ok: true, message: null, remaining: null }

  if (type === 'BEZPLATNY') {
    return { ok: true, message: null, remaining: null }
  }

  if (type === 'NA_ZADANIE') {
    const remaining = balance.byType.NA_ZADANIE.remainingPerYear
    if (workingDays > remaining) {
      return {
        ok: false,
        message: `Brak puli „na żądanie" — pozostało ${remaining} z ${balance.byType.NA_ZADANIE.maxPerYear} dni rocznie, wniosek zajmuje ${workingDays}.`,
        remaining,
      }
    }
    return {
      ok: true,
      message: `Pozostały bilans „na żądanie": ${remaining - workingDays} z ${balance.byType.NA_ZADANIE.maxPerYear} dni rocznie.`,
      remaining,
    }
  }

  if (type === 'OPIEKA_NAD_DZIECKIEM') {
    const remaining = balance.byType.OPIEKA_NAD_DZIECKIEM.remainingPerYear
    if (workingDays > remaining) {
      return {
        ok: false,
        message: `Brak puli „opieka nad dzieckiem" — pozostało ${remaining} z ${balance.byType.OPIEKA_NAD_DZIECKIEM.maxPerYear} dni rocznie, wniosek zajmuje ${workingDays}.`,
        remaining,
      }
    }
    return {
      ok: true,
      message: `Pozostały bilans „opieka nad dzieckiem": ${remaining - workingDays} z ${balance.byType.OPIEKA_NAD_DZIECKIEM.maxPerYear} dni rocznie.`,
      remaining,
    }
  }

  // WYPOCZYNKOWY uses the shared wypoczynkowy/na żądanie pool
  const remaining = balance.balance.remainingDays
  if (workingDays > remaining) {
    return {
      ok: false,
      message: `Brak wystarczającej puli urlopu — pozostało ${remaining} z ${balance.balance.totalDays}, wniosek zajmuje ${workingDays}.`,
      remaining,
    }
  }
  return {
    ok: true,
    message: `Pozostały bilans po zatwierdzeniu: ${remaining - workingDays} z ${balance.balance.totalDays} dni.`,
    remaining,
  }
}

interface RangePreview {
  workingDays: number
  holidays: { date: string; name: string }[]
  weekends: string[]
}

function computeRangePreview(startIso: string, endIso: string): RangePreview {
  const start = parseIsoDate(startIso)
  const end = parseIsoDate(endIso)
  if (end < start) return { workingDays: 0, holidays: [], weekends: [] }
  const wd = calculateWorkingDays(start, end)
  const holidays = listHolidaysInRange(start, end)

  const weekends: string[] = []
  const current = new Date(start.getTime())
  while (current.getTime() <= end.getTime()) {
    const dow = current.getUTCDay()
    if (dow === 0 || dow === 6) {
      const y = current.getUTCFullYear()
      const m = String(current.getUTCMonth() + 1).padStart(2, '0')
      const d = String(current.getUTCDate()).padStart(2, '0')
      weekends.push(`${y}-${m}-${d}`)
    }
    current.setUTCDate(current.getUTCDate() + 1)
  }
  return { workingDays: wd, holidays, weekends }
}

interface VacationRequestDialogProps {
  open: boolean
  onClose: () => void
}

export default function VacationRequestDialog({
  open,
  onClose,
}: VacationRequestDialogProps) {
  const create = useCreateMyVacationRequest()
  const balanceQuery = useMyVacationBalance()
  const session = useSession()
  const canOpieka = session.data?.hasChildrenUnder14 === true
  const availableTypes = (
    ['WYPOCZYNKOWY', 'NA_ZADANIE', 'BEZPLATNY', 'OPIEKA_NAD_DZIECKIEM'] as const
  ).filter((t) => t !== 'OPIEKA_NAD_DZIECKIEM' || canOpieka)

  const today = todayIso()

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      type: 'WYPOCZYNKOWY',
      startDate: '',
      endDate: '',
      comment: '',
    },
  })

  useEffect(() => {
    if (open) {
      form.reset({
        type: 'WYPOCZYNKOWY',
        startDate: '',
        endDate: '',
        comment: '',
      })
    }
  }, [open, form])

  const watchedType = form.watch('type')
  const watchedStart = form.watch('startDate')
  const watchedEnd = form.watch('endDate')

  const preview = useMemo(() => {
    if (!watchedStart || !watchedEnd) {
      return { workingDays: 0, holidays: [], weekends: [] }
    }
    return computeRangePreview(watchedStart, watchedEnd)
  }, [watchedStart, watchedEnd])

  const quota = useMemo(
    () => computeQuota(watchedType, preview.workingDays, balanceQuery.data),
    [watchedType, preview.workingDays, balanceQuery.data],
  )

  async function onSubmit(values: FormValues) {
    if (!quota.ok) return
    if (preview.workingDays === 0) {
      toast.error('Zakres nie zawiera żadnego dnia roboczego.')
      return
    }
    try {
      await create.mutateAsync({
        type: values.type,
        startDate: values.startDate,
        endDate: values.endDate,
        comment: values.comment?.trim() ? values.comment.trim() : undefined,
      })
      toast.success('Wniosek złożony')
      onClose()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="vacation-request-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <header className="flex items-start justify-between gap-4 px-6 py-4 border-b border-border-subtle">
          <h2 id="vacation-request-title" className="text-lg font-semibold">
            Złóż wniosek o urlop
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center h-8 w-8 rounded-action text-text-muted hover:text-text-primary hover:bg-bg-surface transition"
            aria-label="Zamknij"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>

        <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="px-6 py-5 space-y-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Label htmlFor="vacation-type" className="mb-0">
                Typ urlopu
              </Label>
              <PaymentBadge type={watchedType} />
            </div>
            <select
              id="vacation-type"
              className="h-10 w-full rounded-action border border-border-strong bg-bg-elevated px-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              {...form.register('type')}
            >
              {availableTypes.map((t) => (
                <option key={t} value={t}>
                  {TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vacation-start">Data początkowa</Label>
              <input
                id="vacation-start"
                type="date"
                min={today}
                className="h-10 w-full rounded-action border border-border-strong bg-bg-elevated px-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                {...form.register('startDate')}
              />
              {form.formState.errors.startDate && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.startDate.message}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="vacation-end">Data końcowa</Label>
              <input
                id="vacation-end"
                type="date"
                min={watchedStart || today}
                className="h-10 w-full rounded-action border border-border-strong bg-bg-elevated px-3 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
                {...form.register('endDate')}
              />
              {form.formState.errors.endDate && (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.endDate.message}
                </p>
              )}
            </div>
          </div>

          {watchedStart && watchedEnd && watchedEnd >= watchedStart && (
            <RangePreviewSection preview={preview} />
          )}

          <div>
            <Label htmlFor="vacation-comment">
              Komentarz <span className="text-text-faint text-xs">(opcjonalny)</span>
            </Label>
            <textarea
              id="vacation-comment"
              rows={3}
              maxLength={500}
              placeholder="Opcjonalny komentarz dla osoby zatwierdzającej."
              className="w-full rounded-action border border-border-strong bg-bg-elevated px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              {...form.register('comment')}
            />
          </div>

          {quota.message && (
            <div
              className={cn(
                'rounded-card border px-3 py-2 text-sm',
                quota.ok
                  ? 'border-border-subtle bg-bg-surface text-text-muted'
                  : 'border-danger/30 bg-danger/5 text-danger',
              )}
            >
              {!quota.ok && <AlertTriangle className="inline-block h-4 w-4 mr-1 align-text-bottom" aria-hidden />}
              {quota.message}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
            <Button type="button" variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={!quota.ok || preview.workingDays === 0}
            >
              Złóż wniosek
            </Button>
          </div>
          <p className="text-[11px] text-text-faint text-center pt-1">
            {VACATION_PAYMENT_DISCLAIMER}
          </p>
        </form>
      </div>
    </div>
  )
}

function RangePreviewSection({ preview }: { preview: RangePreview }) {
  const weekendSummary = useMemo(() => {
    if (preview.weekends.length === 0) return null
    if (preview.weekends.length === 1) {
      return `${formatHumanDate(preview.weekends[0]!)} (weekend)`
    }
    return `${preview.weekends.length} dni weekendowych`
  }, [preview.weekends])

  return (
    <div className="rounded-card border border-border-subtle bg-bg-surface px-3 py-3 text-sm space-y-2">
      <div className="text-text-primary">
        <span className="font-medium">{preview.workingDays}</span>
        {' '}
        {preview.workingDays === 1 ? 'dzień roboczy' : 'dni roboczych'} w wybranym zakresie
      </div>
      {(preview.holidays.length > 0 || weekendSummary) && (
        <div className="text-text-muted">
          <div className="text-xs uppercase tracking-wider text-text-faint mb-1">
            W zakresie wolne dni
          </div>
          <ul className="space-y-0.5">
            {preview.holidays.map((h) => (
              <li key={h.date}>
                {formatHumanDate(h.date)} ({h.name})
              </li>
            ))}
            {weekendSummary && <li>{weekendSummary}</li>}
          </ul>
        </div>
      )}
    </div>
  )
}
